import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { FETCH_PROJECT_QUERY } from "../../../constants/graphql/queries";

export default function BillingReportViewerModal({ open, onClose, fileUrl, fileName }) {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { data: { project = {} } = {} } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });

  useEffect(() => {
    if (!open || !fileUrl) {
      setLoading(true);
      setError(false);
      return;
    }

    setLoading(true);
    setError(false);
  }, [open, fileUrl]);

  const viewerUrl = useMemo(() => {
    if (!fileUrl) return null;
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
  }, [fileUrl]);

  const handleDownload = () => {
    if (!fileUrl) return;
    window.open(fileUrl, "_blank");
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          width: "100vw",
          height: "100vh",
          maxWidth: "100vw",
          maxHeight: "100vh",
          margin: 0,
          borderRadius: 0,
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        py={1.5}
        gap={2}
        sx={{
          bgcolor: "#1b1d2e",
          color: "#fff",
          minHeight: 56,
        }}
      >
        <Box display="flex" alignItems="center" gap={2} minWidth={0}>
          {project?.name && (
            <Chip
              label={project.name}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.1)",
                color: "white",
                fontSize: 14,
              }}
            />
          )}
          <Typography
            variant="subtitle1"
            component="div"
            noWrap
            sx={{ color: "#fff", fontWeight: 400 }}
          >
            {fileName || "Billing Report"}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={!fileUrl}
            sx={{
              color: "#fff",
              borderColor: "rgba(255, 255, 255, 0.24)",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.4)",
                bgcolor: "rgba(255, 255, 255, 0.08)",
              },
              "&.Mui-disabled": {
                color: "rgba(255, 255, 255, 0.4)",
                borderColor: "rgba(255, 255, 255, 0.12)",
              },
            }}
          >
            Download Excel File
          </Button>
        </Box>
        <Box display="flex" gap={1}>
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, position: "relative", display: "flex", flex: 1, minHeight: 0 }}>
        {!fileUrl && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
            gap={2}
          >
            <Typography variant="body1" color="text.secondary">
              No report selected to preview
            </Typography>
            <Button variant="contained" onClick={onClose}>
              Return to Billing
            </Button>
          </Box>
        )}

        {viewerUrl && !error && (
          <iframe
            src={viewerUrl}
            title={fileName || "Billing Report"}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              visibility: loading ? "hidden" : "visible",
            }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        )}

        {fileUrl && loading && (
          <Box
            position="absolute"
            inset={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="background.paper"
          >
            <Box textAlign="center">
              <CircularProgress />
              <Typography mt={2} color="text.secondary">
                Loading Excel preview...
              </Typography>
            </Box>
          </Box>
        )}

        {fileUrl && error && (
          <Box
            position="absolute"
            inset={0}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            bgcolor="background.paper"
            gap={2}
          >
            <Typography variant="body1" color="text.secondary">
              Unable to preview this file
            </Typography>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
              Download File
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
